/**
 * service-dossiers-en.authored-d.ts — Wave 165
 *
 * Fourth batch of hand-authored English case dossiers (no machine translation,
 * no AI credits required at runtime or build time).
 *
 * Rules enforced here:
 *  - no pricing anywhere (meta or body)
 *  - no Review / AggregateRating / ClaimReview data
 *  - no guarantees; every duration is a working-day range with a caveat
 *  - Apostille wording stays conditional: the Convention enters into force for
 *    Thailand on 28 February 2027; until then the consular legalisation chain
 *    applies.
 */
import type { ServiceDossier } from "./service-dossiers";

const MFA_CITE = {
  label: "Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)",
  url: "https://consular.mfa.go.th/",
};
const DLT_CITE = { label: "Department of Land Transport (Thailand)", url: "https://www.dlt.go.th/" };
const DBD_CITE = { label: "Department of Business Development (DBD)", url: "https://www.dbd.go.th/" };
const DOL_CITE = { label: "Department of Lands (Thailand)", url: "https://www.dol.go.th/" };
const IMMI_CITE = { label: "Immigration Bureau (Thailand)", url: "https://www.immigration.go.th/" };
const MOPH_CITE = { label: "Ministry of Public Health (Thailand)", url: "https://www.moph.go.th/" };

const CASES_LINK: [string, string] = ["All case studies", "/en/case-studies"];

export const EN_AUTHORED_DOSSIERS_D: ServiceDossier[] = [
  {
    slug: "driving-licence-translation-abroad",
    cluster: "translate",
    title: "Driving licence translation for use abroad and in Thailand",
    metaTitle: "Case study: driving licence translation across borders",
    metaDescription:
      "How a Thai driving licence is translated for use abroad, and how a foreign licence is prepared for use in Thailand: documents, agency order, timing and the mistakes that force a restart.",
    h1: "Case study: translating and certifying a driving licence across borders",
    answer:
      "There are two practical routes for driving across borders: an International Driving Permit carried together with the original licence, or a certified translation when the destination does not accept an IDP or when the holder wants to convert to a local licence. The standard bundle is the original licence, the translation, the passport and proof of address. Translation normally takes one to two working days.",
    audience:
      "People relocating for work or study, holders of foreign licences who want to drive or convert in Thailand, and families moving together.",
    facts: [
      { label: "Core documents", value: "Original licence, translation and passport" },
      { label: "Translation time", value: "1–2 working days per set" },
      { label: "MFA certification", value: "Usually 2–5 working days, depending on the filing channel" },
      { label: "Validity", value: "Follows the expiry date printed on the original licence" },
    ],
    documents: [
      "Original, unexpired driving licence — both sides",
      "Passport biographic data page",
      "Translation preserving vehicle categories, issue date and expiry date in full",
      "Proof of address in the destination country where the authority requires it",
      "Medical certificate on the receiving authority's form when converting to a local licence",
    ],
    steps: [
      {
        title: "Confirm whether the destination accepts an IDP or a certified translation",
        detail:
          "Some countries accept an IDP under the Geneva or Vienna conventions; others accept only a certified translation. We check the receiving authority's rule before anything is produced.",
        who: "Advisory team",
        eta: "Same day",
      },
      {
        title: "Translate every field on the licence",
        detail:
          "Vehicle category codes, driving conditions, issue and expiry dates are reproduced exactly, with a translator's note explaining codes the destination may not recognise.",
        who: "Translation team",
        eta: "1–2 working days",
      },
      {
        title: "Certify the translation as the destination requires",
        detail:
          "The Department of Consular Affairs step is added when the receiving authority asks for it; an embassy layer follows only where that country still requires double certification.",
        who: "Department of Consular Affairs / embassy",
        eta: "2–8 working days",
      },
      {
        title: "File the conversion application",
        detail:
          "The bundle is submitted to the transport authority abroad, or to the Department of Land Transport where a foreign licence is being used in Thailand.",
        who: "Destination transport authority / Department of Land Transport",
        eta: "One day to several weeks",
      },
    ],
    pitfalls: [
      "Translating only the front of the card, so the vehicle categories printed on the reverse disappear.",
      "A licence that expires mid-process, which invalidates the translation and certification already produced.",
      "Name spelling in the translation differing from the passport with no translator's note.",
      "Relying on an IDP where the destination in fact requires a certified translation.",
      "Presenting the translation without the original licence, which most authorities require to be shown together.",
    ],
    compare: [
      {
        option: "International Driving Permit (IDP)",
        useWhen: "Short stays where the destination accepts an IDP",
        time: "Same-day issue at the Department of Land Transport",
        note: "Must always be carried with the original Thai licence",
      },
      {
        option: "Certified translation",
        useWhen: "The destination does not accept an IDP, or the licence supports another transaction",
        time: "1–2 working days",
        note: "Suits car rental and general documentary use",
      },
      {
        option: "Translation + MFA (+ embassy) certification",
        useWhen: "Converting to a local licence or filing with a government body",
        time: "3–10 working days",
        note: "Always confirm the receiving authority's requirement before filing",
      },
    ],
    faqs: [
      {
        q: "What is the difference between an IDP and a certified translation?",
        a: "An International Driving Permit is issued by the Department of Land Transport to be carried alongside the Thai licence; it has a limited validity and works only in convention countries. A certified translation reproduces the data on the licence with a statement of accuracy, and is accepted more widely when an authority needs to read the actual details. Many destinations ask for both, so the requirement should be checked before choosing.",
      },
      {
        q: "Can a foreign licence be used in Thailand?",
        a: "For long-term driving or conversion to a Thai licence, the holder files the licence with a certified Thai translation, proof of residence in Thailand, a medical certificate and a passport showing immigration status. Additional testing and the exact criteria depend on the issuing country and the transport office's assessment.",
      },
      {
        q: "Is MFA certification always required?",
        a: "No. Car rental and everyday documentary use are usually satisfied by a certified translation. Filing with a foreign government body or converting to a local licence generally requires certification by the Department of Consular Affairs, and some countries add an embassy layer on top.",
      },
      {
        q: "What if the licence expires while the process is running?",
        a: "Renew the licence before the translation and certification start. Receiving authorities assess the bundle as at the filing date, so an expired original invalidates the work already done and the whole set has to be produced again.",
      },
      {
        q: "Can your team handle all of it?",
        a: "Yes. We confirm the destination authority's rule first, map whether an IDP, a translation or a certification layer is required, then translate, proofread, file for certification and deliver in one cycle. With more than 15 years of case work, the advisory step at the start is what prevents a second round.",
      },
    ],
    related: [
      ["Certified translation", "/en/services/translation"],
      ["MFA legalisation", "/en/services/mfa-legalization"],
      CASES_LINK,
    ],
    citations: [DLT_CITE, MFA_CITE],
  },

  {
    slug: "company-documents-overseas-expansion",
    cluster: "corporate",
    title: "Thai corporate documents for registration and contracts abroad",
    metaTitle: "Case study: Thai corporate documents used overseas",
    metaDescription:
      "Preparing the company affidavit, shareholder list, memorandum and board resolution for translation, certification and filing with foreign authorities, banks and counterparties.",
    h1: "Case study: the Thai corporate document set for overseas expansion",
    answer:
      "Opening a subsidiary, opening a bank account or signing a contract abroad normally requires a recent company affidavit, the shareholder list, the memorandum of association and a board resolution. Each item is translated and certified in the order the destination requires. A full set typically takes five to fifteen working days.",
    audience:
      "Executives, in-house counsel and company secretaries who must present Thai corporate records to a foreign authority, bank or counterparty.",
    facts: [
      { label: "Document age", value: "Affidavits are commonly required to be issued within 30–90 days" },
      { label: "Issuing authority", value: "Department of Business Development (DBD)" },
      { label: "Total duration", value: "5–15 working days, depending on the number of certification layers" },
      { label: "Target language", value: "English in most cases; some countries require their own official language" },
    ],
    documents: [
      "Current company affidavit issued by the DBD",
      "Shareholder list (Bor Or Jor 5)",
      "Memorandum of association and articles of association",
      "Board resolution approving the transaction and naming the authorised signatory",
      "Passport or identification card of the signing director",
      "Power of attorney where a representative will act",
    ],
    steps: [
      {
        title: "Obtain freshly issued records from the DBD",
        detail:
          "The affidavit and shareholder list are re-issued for each transaction because destinations impose an age limit on them.",
        who: "Department of Business Development",
        eta: "Same day to 3 working days",
      },
      {
        title: "Draft the board resolution around the actual transaction",
        detail:
          "The scope of authority, the transaction and the signatory are stated precisely so the receiving party does not read the mandate too narrowly.",
        who: "Company secretary / advisory team",
        eta: "1–3 working days",
      },
      {
        title: "Translate with a single, consistent corporate glossary",
        detail:
          "The company name, officer titles and share classes must be rendered identically across every document, otherwise the reader may treat them as two different entities.",
        who: "Translation team",
        eta: "2–4 working days",
      },
      {
        title: "Certify in the order the destination requires",
        detail:
          "Typically Notary Public, then the Department of Consular Affairs, then the destination country's embassy where that is still required.",
        who: "Notary / MFA / embassy",
        eta: "3–10 working days",
      },
    ],
    pitfalls: [
      "Using an affidavit older than the age limit set by the receiving party.",
      "The company name translated inconsistently between documents in the same bundle.",
      "A board resolution drafted too broadly or too narrowly to cover the real transaction.",
      "Forgetting to translate seals and the certifying officer's signature block on the original.",
      "Filing at the embassy before the MFA step, which breaks the certification order.",
    ],
    compare: [
      {
        option: "Translation with a certificate of accuracy",
        useWhen: "A private counterparty only needs a reliable translation",
        time: "2–4 working days",
        note: "Not sufficient for filing with a foreign government body",
      },
      {
        option: "Notary + MFA certification",
        useWhen: "The destination is a government body or a bank",
        time: "5–10 working days",
        note: "The most common route for corporate work",
      },
      {
        option: "Full chain up to the destination embassy",
        useWhen: "The destination country requires double certification",
        time: "8–15 working days",
        note: "Allow for each embassy's appointment queue",
      },
    ],
    faqs: [
      {
        q: "Does the company affidavit have to be re-issued every time?",
        a: "In practice yes. Most foreign authorities and banks require the affidavit to have been issued within 30 to 90 days of filing, so a previously certified copy is usually rejected and the certification attached to it cannot be reused.",
      },
      {
        q: "Do the articles of association have to be translated in full?",
        a: "It depends on the destination. Bank account opening and subsidiary registration usually require the whole document so the reviewer can check directors' powers and share transfer restrictions; other transactions accept the relevant clauses only. We confirm the scope with the receiving party first so nothing essential is left out and nothing unnecessary is translated.",
      },
      {
        q: "How should the board resolution be drafted?",
        a: "State the meeting date, the quorum, the transaction approved, the signatory's scope of authority and the period during which the mandate is valid. Too broad invites questions about specificity; too narrow forces a fresh meeting when transaction details change, so it is worth drafting with an adviser from the start.",
      },
      {
        q: "Does it help if the destination country is an Apostille member?",
        a: "The destination's membership only shortens the chain when the issuing country can also issue an Apostille. For documents issued in Thailand, follow the status and dates set out on our Apostille page and confirm with the receiving authority before planning.",
      },
      {
        q: "Can your team manage the whole bundle?",
        a: "Yes — from confirming the checklist with the receiving party, obtaining the Thai records, drafting and reviewing the resolution, translating with a consistent glossary, through every certification layer and delivery. Clients do not have to coordinate several agencies themselves, which is where most bundles fail.",
      },
    ],
    related: [
      ["Corporate documents", "/en/services/corporate-documents"],
      ["Notary Public service", "/en/notary"],
      ["MFA legalisation", "/en/services/mfa-legalization"],
      CASES_LINK,
    ],
    citations: [DBD_CITE, MFA_CITE],
  },

  {
    slug: "property-power-of-attorney-thailand",
    cluster: "notary",
    title: "Power of attorney for Thai property transactions signed abroad",
    metaTitle: "Case study: property power of attorney signed abroad",
    metaDescription:
      "How an owner living overseas grants authority for a Land Office transaction in Thailand: forms, where to sign, the certification order, spousal consent and the drafting mistakes that cause rejection.",
    h1: "Case study: a cross-border power of attorney for Thai property",
    answer:
      "An owner living abroad can authorise someone to act at the Thai Land Office by signing before a Thai consular officer or a Notary Public in that country, then having the document certified in Thailand in the correct order. The instrument must identify the property and the scope of authority precisely. A full cycle usually takes seven to twenty working days.",
    audience:
      "Thai nationals and foreign owners living overseas who need to buy, sell, transfer, mortgage or inherit property in Thailand.",
    facts: [
      { label: "Form", value: "The Department of Lands power of attorney form is the safest basis" },
      { label: "Where to sign", value: "Thai embassy or consulate, or a Notary Public abroad" },
      { label: "Total duration", value: "7–20 working days including courier time" },
      { label: "Specificity", value: "Must state the title deed number, the land location and the transaction authorised" },
    ],
    documents: [
      "Completed Department of Lands power of attorney form",
      "Copy of the title deed or other instrument of ownership",
      "Passport or Thai identification card of both grantor and attorney",
      "House registration or proof of address",
      "Marital status evidence or spousal consent where the property is marital property",
      "Certified Thai translations of every foreign-language document",
    ],
    steps: [
      {
        title: "Verify the property record and define the scope",
        detail:
          "Deed number, area and registered owner are checked against the Land Office record before the scope of authority is drafted around the transaction that will actually take place.",
        who: "Advisory team",
        eta: "1–2 working days",
      },
      {
        title: "Sign before an authorised officer",
        detail:
          "Signing takes place at the Thai embassy or consulate, or before a Notary Public in the country of residence. Signing in advance defeats the purpose and the document will be refused.",
        who: "Grantor",
        eta: "Subject to the appointment queue",
      },
      {
        title: "Certify in the correct order",
        detail:
          "A document notarised abroad is certified onward, translated into Thai and then submitted to the Department of Consular Affairs in Thailand.",
        who: "Authorities abroad / MFA",
        eta: "3–10 working days",
      },
      {
        title: "File at the Land Office with the attorney present",
        detail:
          "The attorney presents the full set of originals; the officer checks that names, property details and the scope of authority all match.",
        who: "Land Office",
        eta: "One day to several days depending on the transaction",
      },
    ],
    pitfalls: [
      "A vague grant of authority that omits the deed number and the specific transaction.",
      "Signing before arriving at the embassy or notary, so the signature was not witnessed.",
      "Missing spousal consent where the property is marital property.",
      "Corrections struck through on the form without an initialled amendment.",
      "A translation that uses the wrong term for the type of title, so the officer reads it as a different plot.",
    ],
    compare: [
      {
        option: "Signing at a Thai embassy or consulate",
        useWhen: "The grantor is in a city with a Thai mission",
        time: "Appointment queue + 3–7 days courier",
        note: "Reduces the certification layers needed back in Thailand",
      },
      {
        option: "Notary Public abroad plus onward certification",
        useWhen: "No Thai mission is reachable",
        time: "7–20 working days",
        note: "Requires Thai translation and further certification before Land Office use",
      },
      {
        option: "Travelling to Thailand to sign in person",
        useWhen: "The transaction is complex or high value",
        time: "Subject to travel plans",
        note: "Removes almost all scope-of-authority risk",
      },
    ],
    faqs: [
      {
        q: "Must the Department of Lands form be used?",
        a: "It is the safest option because officers recognise the layout and the required fields. A foreign-style deed of power can be used, but it needs a complete Thai translation and must identify the property and the scope of authority at least as precisely as the official form, otherwise rejection is a real risk.",
      },
      {
        q: "Does the attorney have to be a Thai national?",
        a: "Nationality is not the restriction. In practice the attorney must appear at the Land Office with identification and be able to deal with the officer's questions, so owners usually appoint someone resident in Thailand who understands the procedure and can correct paperwork on the spot.",
      },
      {
        q: "What if the spouse is a foreign national?",
        a: "Where marital status affects the property, officers commonly ask for spousal consent together with status evidence. If the spouse is foreign, those documents follow the same translation and certification chain as the power of attorney, so it is efficient to prepare them in the same cycle rather than couriering documents twice.",
      },
      {
        q: "How long does the whole process take?",
        a: "Seven to twenty working days is typical. The variables are the appointment queue at the embassy or notary abroad and the courier time for the originals; the Thai translation and certification steps usually take only a few working days.",
      },
      {
        q: "How much can your team take over?",
        a: "From checking the property record, drafting the scope of authority around the transaction, preparing the bilingual bundle and coordinating the appointment, through certification in Thailand and attending the Land Office with the attorney. Owners abroad can complete the transaction without flying back.",
      },
    ],
    related: [
      ["Notary Public service", "/en/notary"],
      ["Certified translation", "/en/services/translation"],
      ["MFA legalisation", "/en/services/mfa-legalization"],
      CASES_LINK,
    ],
    citations: [DOL_CITE, MFA_CITE],
  },

  {
    slug: "retirement-visa-document-set",
    cluster: "visa",
    title: "The retirement visa document set in Thailand",
    metaTitle: "Case study: retirement visa documents in Thailand",
    metaDescription:
      "Preparing a Non-O retirement application in Thailand: the financial evidence routes, foreign documents that must be certified and translated, the filing office rule and common refusal reasons.",
    h1: "Case study: assembling a retirement visa document set in Thailand",
    answer:
      "A retirement applicant must meet the age criterion and show financial evidence in a form the Immigration Bureau accepts, together with proof of accommodation in Thailand. Foreign documents such as a pension certificate must be certified by the applicant's own embassy and translated into Thai before they are filed. Preparation typically runs seven to twenty-one working days, driven by embassy queues.",
    audience:
      "Retired foreign nationals planning long-term residence in Thailand, and family members handling the paperwork for them.",
    facts: [
      { label: "Deciding authority", value: "Immigration Bureau" },
      { label: "Financial evidence", value: "Thai bank deposit, regular income, or a combination, per the applicable criteria" },
      { label: "Foreign documents", value: "Certified by the applicant's embassy and translated into Thai" },
      { label: "Preparation time", value: "7–21 working days, subject to embassy queues" },
    ],
    documents: [
      "Passport with sufficient remaining validity and copies of every stamped page",
      "Application form and photographs to specification",
      "Financial evidence such as a Thai bank passbook and a bank confirmation letter",
      "Income or pension certificate from the home country, certified by that embassy",
      "Certified Thai translations of the foreign documents",
      "Proof of accommodation in Thailand, such as a lease and the residence notification",
    ],
    steps: [
      {
        title: "Check eligibility and choose the financial route",
        detail:
          "Deposit, regular income or a combination — the choice determines how far in advance funds must be seasoned in the account before filing.",
        who: "Applicant / advisory team",
        eta: "Plan 2–3 months ahead",
      },
      {
        title: "Obtain the income certificate from the applicant's embassy",
        detail:
          "Formats and conditions differ by country, and several embassies no longer issue them at all, in which case the deposit route replaces it.",
        who: "Applicant's embassy",
        eta: "Subject to the appointment queue",
      },
      {
        title: "Translate the foreign documents into Thai",
        detail:
          "Certificates and supporting papers are translated with figures and institution names reproduced exactly, with a statement of accuracy attached.",
        who: "Translation team",
        eta: "1–3 working days",
      },
      {
        title: "File and comply with the reporting schedule",
        detail:
          "The bundle is filed at the immigration office covering the applicant's actual residence, and the reporting obligations begin once permission is granted.",
        who: "Immigration Bureau",
        eta: "As scheduled by the office",
      },
    ],
    pitfalls: [
      "Funds not held in the account for the required period before filing.",
      "A bank account not held in the applicant's sole name.",
      "A bank confirmation letter issued too long before the filing date.",
      "Foreign documents that have not been certified by the applicant's own embassy.",
      "An address that does not match the residence notification, causing the file to be lodged at the wrong office.",
    ],
    compare: [
      {
        option: "Thai bank deposit route",
        useWhen: "Funds can be transferred to Thailand well in advance",
        time: "Several months of planning",
        note: "The balance must be maintained as required and the bank letter must be recent",
      },
      {
        option: "Income or pension route",
        useWhen: "The applicant's embassy still issues income certificates",
        time: "Embassy queue + 1–3 days translation",
        note: "No lump sum needs to sit in an account, but the certificate must be in the correct form",
      },
      {
        option: "Combination route",
        useWhen: "Neither income nor deposit alone reaches the threshold",
        time: "Similar to the two routes above",
        note: "Both sides of the evidence must be consistent with each other",
      },
    ],
    faqs: [
      {
        q: "How far in advance should preparation start?",
        a: "At least two to three months. The deposit route requires funds to be seasoned in the account before filing, and embassy appointments for an income certificate can run long. Starting late usually means postponing the filing date, which then collides with the end of the current permission to stay.",
      },
      {
        q: "What if the embassy no longer issues income certificates?",
        a: "Several countries have stopped issuing them. Applicants then move to the Thai bank deposit route, or a combination showing both deposits and other certified income evidence. Check the current practice of the immigration office covering your address before committing to a route.",
      },
      {
        q: "Do all foreign documents need Thai translations?",
        a: "Any foreign-language document relied on should be filed with an accurate Thai translation. Even where an officer reads English, the translation removes ambiguity about figures and institution names and speeds up the review.",
      },
      {
        q: "Which office handles the filing?",
        a: "The immigration office responsible for the area where the applicant actually resides, as notified. Filing at the wrong office restarts the process, so the lease, the residence notification and every supporting document must show the same address before you travel to file.",
      },
      {
        q: "What can your team do?",
        a: "We assess which route fits the applicant's nationality and finances, build the preparation timeline, coordinate embassy appointments, translate and certify the foreign documents, and review the complete bundle before the filing date so nothing extra is requested at the counter.",
      },
    ],
    related: [
      ["Thai visa services", "/en/services/thai-visa"],
      ["Certified translation", "/en/services/translation"],
      CASES_LINK,
    ],
    citations: [IMMI_CITE, MFA_CITE],
  },

  {
    slug: "medical-records-translation-overseas-treatment",
    cluster: "translate",
    title: "Medical record translation for treatment abroad",
    metaTitle: "Case study: medical records translated for treatment abroad",
    metaDescription:
      "Translating medical summaries, laboratory results, imaging reports and doctor's certificates for hospitals and insurers abroad, with terminology, unit and privacy safeguards.",
    h1: "Case study: translating medical records for continued treatment overseas",
    answer:
      "Referring a patient abroad requires the medical summary, laboratory results, imaging reports and the treating doctor's certificate. All of them must be translated with clinical terminology, reference ranges and units preserved exactly as issued. A set normally takes two to five working days, and the records should be requested from the hospital as one bundle.",
    audience:
      "Patients and families arranging treatment abroad, international health insurance claimants, and visa applicants who must file medical evidence.",
    facts: [
      { label: "Core documents", value: "Medical summary, laboratory results, imaging reports, doctor's certificate" },
      { label: "Translation time", value: "2–5 working days per set" },
      { label: "Special requirement", value: "Units and reference ranges are preserved as issued" },
      { label: "Privacy", value: "Health data is sensitive personal data under Thai data protection law" },
    ],
    documents: [
      "Medical summary signed by the treating physician",
      "Laboratory results including the laboratory's reference ranges",
      "Imaging reports such as CT, MRI or X-ray",
      "Doctor's certificate stating the diagnosis and the onward treatment plan",
      "Current medication list with dosages",
      "Consent to disclose health information where a representative is acting",
    ],
    steps: [
      {
        title: "Request the records from the hospital as one set",
        detail:
          "The medical record request follows the hospital's own procedure and states that the purpose is a referral for treatment abroad.",
        who: "Originating hospital",
        eta: "3–15 working days",
      },
      {
        title: "Translate with medical specialists",
        detail:
          "Diagnostic terms, disease codes, dosages and units are preserved, with a note wherever a unit differs from the destination's convention.",
        who: "Specialist translation team",
        eta: "2–5 working days",
      },
      {
        title: "Independent review line by line",
        detail:
          "Figures, dates and drug names are checked against the source on every line, because a numeric discrepancy has clinical consequences.",
        who: "Review team",
        eta: "Within 1 working day",
      },
      {
        title: "Add certification where the recipient requires it",
        detail:
          "Some hospitals and insurers require a certificate of accuracy or certification by the Department of Consular Affairs.",
        who: "Our team / MFA",
        eta: "2–7 working days",
      },
    ],
    pitfalls: [
      "Translating only the doctor's certificate without the test results the diagnosis rests on.",
      "Converting units without a note, so the destination reads a different value.",
      "Using brand names instead of generic drug names, so the destination cannot match the medicine.",
      "Records missing the physician's signature or the hospital seal.",
      "Sending health data over an insecure channel and without a consent form.",
    ],
    compare: [
      {
        option: "Certified translation only",
        useWhen: "The receiving hospital accepts a statement of accuracy",
        time: "2–5 working days",
        note: "The most common route for direct hospital-to-hospital referral",
      },
      {
        option: "Translation with additional certification",
        useWhen: "An insurer or a public hospital abroad requires it",
        time: "5–12 working days",
        note: "Confirm the required layer before the records are certified",
      },
      {
        option: "Summary translation for triage first",
        useWhen: "A second opinion is needed before the full referral",
        time: "1–2 working days",
        note: "Full records still follow before treatment begins",
      },
    ],
    faqs: [
      {
        q: "Which documents does the receiving hospital actually need?",
        a: "Usually the medical summary, the laboratory results with reference ranges, the imaging reports and the treating physician's certificate with the onward plan. Sending the certificate alone is the most common cause of a follow-up request, because the receiving clinician cannot verify the diagnosis without the underlying results.",
      },
      {
        q: "Should units be converted to the destination's convention?",
        a: "The translation preserves the source units and reference ranges, and adds a translator's note where the convention differs. Silent conversion is unsafe: the receiving clinician expects to see the values exactly as the issuing laboratory reported them.",
      },
      {
        q: "How is privacy handled?",
        a: "Health data is sensitive personal data. Files are exchanged through controlled channels, access is limited to the assigned translators and reviewers, and a signed consent is obtained where a family member or agent is acting on the patient's behalf.",
      },
      {
        q: "How long does the hospital take to release records?",
        a: "Between three and fifteen working days in most cases, depending on the hospital's medical record department and how far back the history goes. Requesting everything in one application, rather than department by department, is the single biggest time saver.",
      },
      {
        q: "Can your team coordinate the whole referral paperwork?",
        a: "Yes. We help draft the record request, translate with specialist reviewers, run the numeric check, arrange any certification the recipient requires and deliver in the format the receiving hospital or insurer asks for — so the family can focus on the treatment rather than the paperwork.",
      },
    ],
    related: [
      ["Certified translation", "/en/services/translation"],
      ["MFA legalisation", "/en/services/mfa-legalization"],
      CASES_LINK,
    ],
    citations: [MOPH_CITE, MFA_CITE],
  },
];
